import type { PieChartModel } from "@/types/dashboard";

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number
): string {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
}

interface SvgPieChartProps {
  data: PieChartModel[];
  otherHoverItems?: string[];
  selectedBusiness?: string | null;
}

export function SvgPieChart({
  data,
  otherHoverItems = [],
  selectedBusiness
}: SvgPieChartProps) {
  if (!data || data.length === 0) return null;

  const total = data.reduce((sum, d) => sum + d.segmentValue, 0);
  if (total === 0) return null;

  let currentAngle = 0;

  return (
    <svg viewBox="0 0 200 200" role="img" aria-label="Pie chart" className="h-full w-full">
      {data.map((segment) => {
        const sliceAngle = (segment.segmentValue / total) * 360;
        const path = arcPath(100, 100, 90, currentAngle, currentAngle + sliceAngle);
        currentAngle += sliceAngle;

        const isOther =
          segment.segmentName.toLowerCase() === "other" && !selectedBusiness;
        const tooltipText = isOther
          ? otherHoverItems.join(", ")
          : `${segment.segmentName} | ${segment.segmentValueString}`;

        return (
          <path
            key={segment.segmentName}
            d={path}
            fill={segment.segmentColor}
            stroke="white"
            strokeWidth="1.5"
            className="transition-opacity hover:opacity-80"
          >
            <title>{tooltipText}</title>
          </path>
        );
      })}
    </svg>
  );
}
